import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import ItemTicket from "./ItemTicket";
import { toast } from "react-toastify";
import { reservations, subscriptions } from "../apiServices/apiServices";
import CustomToastContainer from "./CustomToastContainer";
import { WorkspacePremiumRounded } from "@mui/icons-material";
import ItemSubscription from "./ItemSubscription";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

export default function SeatBookingTabs() {
  const [value, setValue] = useState(1);
  const [userData, setUserData] = useState({});
  const [reservationsData, setReservationsData] = useState([]);
  const [pastReservations, setPastReservations] = useState([]);
  const [todayReservations, setTodayReservations] = useState([]);
  const [upcomingReservations, setUpcomingReservations] = useState([]);
  const [subscriptionsList, setSubscriptions] = useState([]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const user = window.localStorage.getItem("user");
    // console.log("User : ", user);
    if (user) {
      const parsedUser = JSON.parse(user);
      setUserData(parsedUser);
      reservationsMethod(parsedUser.id);
      subscriptionsMethod(parsedUser.id);
    } else {
      setUserData({});
    }
  }, []);

  const reservationsMethod = async (userId) => {
    try {
      const response = await reservations(userId);
      if (response && response.success) {
        const reservationList = response.data.reservations;
        // console.log("Reservations : ", reservationList);
        setReservationsData(reservationList);
        divideReservations(reservationList);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };
  const subscriptionsMethod = async (userId) => {
    try {
      const response = await subscriptions(userId);
      if (response && response.success) {
        const subscriptionsList = response.data.reservations;
        setSubscriptions(subscriptionsList);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const divideReservations = (reservationList) => {
    const now = new Date();
    const past = [];
    const today = [];
    const upcoming = [];

    reservationList.forEach((reservation) => {
      const startDate = new Date(reservation.start_date);
      const endDate = new Date(reservation.end_date);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      now.setHours(0, 0, 0, 0);

      if (endDate < now) {
        past.push(reservation);
      } else if (startDate.toDateString() === now.toDateString()) {
        today.push(reservation);
      } else if (startDate > now) {
        upcoming.push(reservation);
      }
    });

    setPastReservations(past);
    setTodayReservations(today);

    const sortedUpcoming = [...upcoming].sort(
      (b, a) =>
        new Date(a.start_date.replace(" ", "T")) -
        new Date(b.start_date.replace(" ", "T")),
    );

    setUpcomingReservations(sortedUpcoming);
  };

  return (
    <>
      <AppBar
        position="sticky"
        sx={{ backgroundColor: "white", boxShadow: "none" }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{ style: { backgroundColor: "#BE1F18" } }}
          variant="fullWidth"
          aria-label="full width tabs example"
        >
          <Tab
            label="Past"
            {...a11yProps(0)}
            sx={{
              color: value === 0 ? "#BE1F18" : "gray",
              fontWeight: "bold",
              "&.Mui-selected": {
                color: "#BE1F18",
              },
            }}
          />
          <Tab
            label="Today"
            {...a11yProps(1)}
            sx={{
              color: value === 1 ? "#BE1F18" : "gray",
              fontWeight: "bold",
              "&.Mui-selected": {
                color: "#BE1F18",
              },
            }}
          />
          <Tab
            label="Upcoming"
            {...a11yProps(2)}
            sx={{
              color: value === 2 ? "#BE1F18" : "gray",
              fontWeight: "bold",
              "&.Mui-selected": {
                color: "#BE1F18",
              },
            }}
          />
          <Tab
            icon={<WorkspacePremiumRounded />}
            {...a11yProps(3)}
            sx={{
              color: value === 3 ? "#BE1F18" : "gray",
              fontWeight: "bold",
              "&.Mui-selected": {
                color: "#BE1F18",
              },
            }}
          />
        </Tabs>
      </AppBar>

      <Box sx={{ flexGrow: 1, width: "100%" }}>
        <TabPanel value={value} index={0}>
          {pastReservations
            .slice()
            .reverse()
            .map((reservation) => (
              <ItemTicket
                key={reservation.reservation_id}
                routeName={reservation.route_name}
                passengerName={reservation.fullName}
                gender={reservation.gender}
                pickupStopName={reservation.pickup_stop_name}
                dropoffStopName={reservation.dropoff_stop_name}
                seatNo={reservation.seat_no}
                bookingDate={reservation.start_date}
                // Additional fields
                pickupLat={reservation.pickup_lat}
                pickupLon={reservation.pickup_long}
                dropOffLat={reservation.dropoff_lat}
                dropOffLon={reservation.dropoff_long}
                pickupStopId={reservation.pickup_stop_id}
                dropoffStopId={reservation.dropoff_stop_id}
                routeId={reservation.route_id}
                status={reservation.status}
                reOrder={true}
                cancel={false}
                onRefresh={() => reservationsMethod(userData.id)}
              />
            ))}
        </TabPanel>
        <TabPanel value={value} index={1}>
          {todayReservations
            .slice()
            .reverse()
            .map((reservation) => (
              <ItemTicket
                key={reservation.reservation_id}
                routeName={reservation.route_name}
                passengerName={reservation.fullName}
                gender={reservation.gender}
                pickupStopName={reservation.pickup_stop_name}
                dropoffStopName={reservation.dropoff_stop_name}
                seatNo={reservation.seat_no}
                bookingDate={reservation.start_date}
                // Additional fields
                pickupLat={reservation.pickup_lat}
                pickupLon={reservation.pickup_long}
                dropOffLat={reservation.dropoff_lat}
                dropOffLon={reservation.dropoff_long}
                pickupStopId={reservation.pickup_stop_id}
                dropoffStopId={reservation.dropoff_stop_id}
                routeId={reservation.route_id}
                reservationId={reservation.reservation_id}
                status={reservation.status}
                cancel={true}
                onRefresh={() => reservationsMethod(userData.id)}
              />
            ))}
        </TabPanel>
        <TabPanel value={value} index={2}>
          {upcomingReservations
            .slice()
            .reverse()
            .map((reservation) => (
              <ItemTicket
                key={reservation.reservation_id}
                routeName={reservation.route_name}
                passengerName={reservation.fullName}
                gender={reservation.gender}
                pickupStopName={reservation.pickup_stop_name}
                dropoffStopName={reservation.dropoff_stop_name}
                seatNo={reservation.seat_no}
                bookingDate={reservation.start_date}
                // Additional fields
                pickupLat={reservation.pickup_lat}
                pickupLon={reservation.pickup_long}
                dropOffLat={reservation.dropoff_lat}
                dropOffLon={reservation.dropoff_long}
                pickupStopId={reservation.pickup_stop_id}
                dropoffStopId={reservation.dropoff_stop_id}
                routeId={reservation.route_id}
                reservationId={reservation.reservation_id}
                status={reservation.status}
                cancel={true}
                onRefresh={() => reservationsMethod(userData.id)}
              />
            ))}
        </TabPanel>
        <TabPanel value={value} index={3}>
          {subscriptionsList.map((reservation) => (
            <ItemSubscription
              subscription={reservation}
              subscriptionRenewed={() => {
                subscriptionsMethod(userData.id);
              }}
            />
          ))}
        </TabPanel>
      </Box>
      <CustomToastContainer />
    </>
  );
}
